import { Link, usePage } from "@inertiajs/react"
import { useEffect, useState } from "react"

import { CRTOverlay } from "@/components/crt-overlay"
import { Button } from "@/components/ui/button"

import { NeonTraceTitle } from "./neon-trace-title"

/* ─────────────────────────────────────────────────────────
 * ANIMATION STORYBOARD — Hero Entrance
 *
 * Read top-to-bottom. Each `at` value is ms after mount.
 *
 *    0ms   Black screen. Nothing visible.
 *  300ms   Stadium photo fades in + slow zoom starts
 *          Auth entry (top-right) fades in with stadium
 *  600ms   Tagline line 1 fades up
 * 1200ms   Tagline line 2 fades up (gold)
 * 2200ms   Scroll indicator fades in + pulse begins
 *
 * The SVG title trace runs on its own infinite CSS loop —
 * it is not part of the stage sequence.
 * ───────────────────────────────────────────────────────── */

const TIMING = {
  bgReveal: 300, // stadium photo fade-in starts
  line1: 600, // first tagline
  line2: 1200, // second tagline (gold)
  scrollIndicator: 2200, // scroll indicator appears
} as const

const BG = {
  fadeDuration: 2000, // ms — fade-in duration
  zoomDuration: 30000, // ms — slow Ken Burns zoom
  scaleFrom: 1.15, // starting scale
  scaleTo: 1.23, // ending scale after zoom
} as const

const TAGLINE = {
  offsetY: 20, // px — slides up from
  fadeDuration: 800, // ms
} as const

const SCROLL = {
  fadeDuration: 600, // ms — fade-in duration
  pulseDuration: 2000, // ms — infinite pulse cycle
} as const

interface HeroSectionProps {
  heroImageUrl?: string | null
}

export function HeroSection({ heroImageUrl }: HeroSectionProps) {
  const { auth } = usePage<{ auth: { user: unknown } }>().props
  const isAuthenticated = !!auth?.user
  const [stage, setStage] = useState(0)

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), TIMING.bgReveal),
      setTimeout(() => setStage(2), TIMING.line1),
      setTimeout(() => setStage(3), TIMING.line2),
      setTimeout(() => setStage(4), TIMING.scrollIndicator),
    ]
    return () => timers.forEach(clearTimeout)
  }, [])

  return (
    <section
      className="relative overflow-hidden"
      style={{ width: "100vw", marginLeft: "calc(-50vw + 50%)" }}
    >
      {/* Continuous CSS animations that don't fit the stage pattern */}
      <style>{`
        @keyframes hero-zoom {
          from { transform: scale(${BG.scaleFrom}); }
          to   { transform: scale(${BG.scaleTo}); }
        }
        @keyframes scroll-pulse {
          0%, 100% { opacity: 0.4; transform: translateY(0); }
          50%      { opacity: 0.8; transform: translateY(4px); }
        }
      `}</style>

      <div
        className="relative flex min-h-screen flex-col items-center justify-end px-4 pb-16 md:pb-24"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, oklch(0.14 0.02 55), oklch(0.06 0.01 55))",
        }}
      >
        {/* Stadium panorama background — slow zoom */}
        {heroImageUrl && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `url('${heroImageUrl}')`,
              backgroundSize: "cover",
              backgroundPosition: "center 30%",
              transformOrigin: "center 30%",
              opacity: stage >= 1 ? 1 : 0,
              transition:
                stage >= 1 ? `opacity ${BG.fadeDuration}ms ease-out` : "none",
              animation:
                stage >= 1
                  ? `hero-zoom ${BG.zoomDuration}ms ease-out forwards`
                  : "none",
            }}
          />
        )}
        {/* Gradient overlay */}
        {heroImageUrl && (
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, oklch(0.04 0.01 55 / 0.15) 0%, oklch(0.04 0.01 55 / 0.1) 25%, oklch(0.04 0.01 55 / 0.4) 55%, oklch(0.04 0.01 55 / 0.92) 85%, oklch(0.04 0.01 55) 100%)",
            }}
          />
        )}

        <CRTOverlay intensity={0.7} />

        {/* Gold glow line at top */}
        <div
          className="pointer-events-none absolute top-0 right-0 left-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 10%, oklch(0.85 0.28 85 / 0.4) 30%, oklch(0.85 0.28 85 / 0.6) 50%, oklch(0.85 0.28 85 / 0.4) 70%, transparent 90%)",
            boxShadow: "0 0 12px oklch(0.85 0.28 85 / 0.3)",
            zIndex: 3,
          }}
        />

        {/* Auth entry — top right */}
        <div
          className="absolute top-4 right-4 z-20 md:top-6 md:right-6"
          style={{
            opacity: stage >= 1 ? 1 : 0,
            transition:
              stage >= 1 ? `opacity ${BG.fadeDuration}ms ease-out` : "none",
          }}
        >
          <Link href={isAuthenticated ? "/dashboard" : "/sign_in"}>
            <Button variant="gold" size="sm">
              {isAuthenticated ? "Coach's Hub" : "Coach In"}
            </Button>
          </Link>
        </div>

        {/* Title — pinned to top, full width */}
        <div className="absolute top-0 right-0 left-0 z-10 flex justify-center pt-16 md:pt-24">
          <h1 className="w-full max-w-6xl px-4">
            <NeonTraceTitle
              id="title"
              text="NUFFLE CLUB"
              viewBox="0 0 1200 150"
              fontSize={130}
              letterSpacing={22}
              glowBlur={6}
              textGlowBlur={8}
              goldStrokeWidth={16}
              greenStrokeWidth={13}
              ariaLabel="Nuffle Club"
            />
          </h1>
        </div>

        {/* Tagline + scroll indicator — anchored to bottom */}
        <div className="relative z-10 flex flex-col items-center">
          <div className="max-w-3xl text-center">
            <p
              className="font-display text-base leading-relaxed tracking-[0.08em] uppercase md:text-xl"
              style={{
                color: "oklch(0.72 0.02 80)",
                textShadow:
                  "0 2px 12px oklch(0 0 0 / 0.8), 0 4px 8px oklch(0 0 0 / 0.6)",
                opacity: stage >= 2 ? 1 : 0,
                transform: `translateY(${stage >= 2 ? 0 : TAGLINE.offsetY}px)`,
                transition:
                  stage >= 2
                    ? `opacity ${TAGLINE.fadeDuration}ms ease-out, transform ${TAGLINE.fadeDuration}ms ease-out`
                    : "none",
              }}
            >
              Orcs, skaven, and the risen dead on a blood-soaked pitch
            </p>

            <p
              className="font-display mt-2 text-xl leading-tight tracking-[0.08em] uppercase md:mt-4 md:text-3xl"
              style={{
                color: "oklch(0.85 0.28 85)",
                textShadow:
                  "0 0 20px oklch(0.85 0.28 85 / 0.6), 0 0 40px oklch(0.85 0.28 85 / 0.3), 0 2px 12px oklch(0 0 0 / 0.8)",
                opacity: stage >= 3 ? 1 : 0,
                transform: `translateY(${stage >= 3 ? 0 : TAGLINE.offsetY}px)`,
                transition:
                  stage >= 3
                    ? `opacity ${TAGLINE.fadeDuration}ms ease-out, transform ${TAGLINE.fadeDuration}ms ease-out`
                    : "none",
              }}
            >
              where a spiteful god rolls the dice.
            </p>
          </div>

          {/* Scroll indicator */}
          <div
            className="mt-10 flex flex-col items-center"
            style={{
              opacity: stage >= 4 ? 1 : 0,
              transition:
                stage >= 4
                  ? `opacity ${SCROLL.fadeDuration}ms ease-out`
                  : "none",
            }}
          >
            <div
              style={{
                color: "oklch(0.85 0.28 85 / 0.6)",
                animation:
                  stage >= 4
                    ? `scroll-pulse ${SCROLL.pulseDuration}ms ease-in-out infinite`
                    : "none",
              }}
            >
              <svg width="24" height="14" viewBox="0 0 24 14" fill="none">
                <path
                  d="M2 2L12 12L22 2"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
